import React, { useEffect, useState } from 'react';
import { G, styles, api } from '../theme.js';
import { Btn, Badge, Field, Input, Select, Modal, Spinner, Textarea } from '../ui.jsx';

export function Reviews({ toast }) {
  const [data, setData] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api('/api/reviews').then(setData).catch(e => toast(e.message, 'error'));
  useEffect(() => { load(); }, []);

  if (!data) return <div style={{ padding:40 }}><Spinner sz={32}/></div>;

  const { summary, reviews } = data;
  const stars = (n) => '★★★★★'.slice(0, n) + '☆☆☆☆☆'.slice(0, 5 - n);

  return <div style={{ padding:24 }}>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:18 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Reviews <span style={{ color:G.textMuted, fontWeight:500, fontSize:16 }}>({summary.total})</span></h1>
      <div style={{ display:'flex', gap:8 }}>
        <Btn onClick={()=>setAddOpen(true)}>+ Log External Review</Btn>
      </div>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))', gap:14, marginBottom:18 }}>
      <Stat label="Average rating" value={`${summary.avg.toFixed(1)} ★`} color={summary.avg>=4.5?G.green:summary.avg>=4?G.cyan:G.yellow}/>
      <Stat label="Total reviews" value={summary.total} color={G.purple}/>
      <Stat label="Pending response" value={summary.pendingResponse} color={summary.pendingResponse>0?G.yellow:G.green}/>
      <Stat label="From Google" value={summary.bySource.google||0} color={G.green}/>
      <Stat label="From Yelp" value={summary.bySource.yelp||0} color={G.red}/>
      <Stat label="From Facebook" value={summary.bySource.facebook||0} color={G.blue}/>
    </div>

    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
      {reviews.length === 0 && <div style={{ ...styles.card, padding:40, textAlign:'center', color:G.textMuted }}>
        No reviews yet. They'll auto-collect as jobs complete (4-5★ → public Google review; 1-3★ → private feedback).
      </div>}
      {reviews.map(r => <div key={r.id} style={{ ...styles.card, padding:16 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
          <div>
            <div style={{ fontSize:20, color:r.rating>=4?G.green:r.rating>=3?G.yellow:G.red, fontWeight:700, letterSpacing:'.05em' }}>{stars(r.rating||0)}</div>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>
              {r.author_name ? `${r.author_name} · ` : ''}
              <Badge c={sourceColor(r.source)} bg={sourceColor(r.source)+'22'}>{r.source||'in_app'}</Badge>
              {r.published_at && <span style={{ marginLeft:8 }}>{new Date(r.published_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <Btn sz="xs" onClick={()=>setEditing(r)}>Respond</Btn>
        </div>
        {r.comment && <div style={{ padding:'8px 12px', background:G.input, borderRadius:8, fontSize:13, color:G.text, lineHeight:1.55, marginBottom:r.response?6:0 }}>
          "{r.comment}"
        </div>}
        {r.response && <div style={{ padding:'8px 12px', background:G.purpleDim, borderRadius:8, fontSize:13, color:G.text, lineHeight:1.55, marginTop:6 }}>
          <div style={{ fontSize:11, color:G.purple, fontWeight:700, textTransform:'uppercase', letterSpacing:'.06em', marginBottom:4 }}>
            Your response {r.response_published ? '· published' : '· draft'}
          </div>
          {r.response}
        </div>}
      </div>)}
    </div>

    <AddExternalModal open={addOpen} onClose={()=>setAddOpen(false)} onSaved={()=>{ setAddOpen(false); load(); toast('Logged review'); }}/>
    <EditResponseModal review={editing} onClose={()=>setEditing(null)} onSaved={()=>{ setEditing(null); load(); }}/>
  </div>;
}

function Stat({ label, value, color }) {
  return <div style={{ ...styles.card, padding:14 }}>
    <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', fontWeight:700, letterSpacing:'.06em' }}>{label}</div>
    <div style={{ fontSize:24, fontWeight:800, color, marginTop:4 }}>{value}</div>
  </div>;
}

function sourceColor(s) {
  return s === 'google' ? G.green : s === 'yelp' ? G.red : s === 'facebook' ? G.blue : G.purple;
}

function AddExternalModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ source:'google', rating:5, comment:'', authorName:'', sourceUrl:'' });
  if (!open) return null;
  const submit = async () => {
    if (!form.rating) return alert('Pick a rating');
    try { await api('/api/reviews/external', { method:'POST', body: form }); onSaved(); }
    catch (e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={520}>
    <h2 style={{ margin:'0 0 14px' }}>Log a review from elsewhere</h2>
    <Field label="Source">
      <Select value={form.source} onChange={e=>setForm({...form, source:e.target.value})}>
        <option value="google">Google Business Profile</option>
        <option value="yelp">Yelp</option>
        <option value="facebook">Facebook</option>
        <option value="bbb">BBB</option>
        <option value="angi">Angi</option>
        <option value="other">Other</option>
      </Select>
    </Field>
    <Field label="Rating (1-5)">
      <Select value={form.rating} onChange={e=>setForm({...form, rating:Number(e.target.value)})}>
        {[5,4,3,2,1].map(n => <option key={n} value={n}>{'★'.repeat(n) + '☆'.repeat(5-n)} ({n})</option>)}
      </Select>
    </Field>
    <Field label="Author"><Input value={form.authorName} onChange={e=>setForm({...form, authorName:e.target.value})} placeholder="Jane S."/></Field>
    <Field label="Review text"><Textarea value={form.comment} onChange={e=>setForm({...form, comment:e.target.value})} style={{ minHeight:100 }}/></Field>
    <Field label="Source URL (optional)"><Input value={form.sourceUrl} onChange={e=>setForm({...form, sourceUrl:e.target.value})} placeholder="https://www.google.com/maps/..."/></Field>
    <div style={{ display:'flex', justifyContent:'flex-end', gap:10, marginTop:14 }}>
      <Btn onClick={onClose}>Cancel</Btn>
      <Btn v="primary" onClick={submit}>Log + Draft AI Response</Btn>
    </div>
  </Modal>;
}

function EditResponseModal({ review, onClose, onSaved }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setText(review?.response || ''); }, [review]);
  if (!review) return null;
  const regen = async () => {
    setBusy(true);
    try { const r = await api(`/api/reviews/${review.id}/regenerate-response`, { method:'POST' }); setText(r.response||''); }
    catch (e) { alert(e.message); }
    finally { setBusy(false); }
  };
  const save = async (publish) => {
    try { await api(`/api/reviews/${review.id}`, { method:'PATCH', body:{ response: text, responsePublished: publish }}); onSaved(); }
    catch (e) { alert(e.message); }
  };
  return <Modal open onClose={onClose} width={560}>
    <h2 style={{ margin:'0 0 8px' }}>Respond to review</h2>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>
      {review.rating}★ from {review.author_name||'anonymous'} on {review.source}
    </div>
    <div style={{ padding:10, background:G.input, borderRadius:8, fontSize:13, marginBottom:14, lineHeight:1.55 }}>"{review.comment||'(no comment)'}"</div>
    <Field label="Your response (AI-drafted, edit freely)">
      <Textarea value={text} onChange={e=>setText(e.target.value)} style={{ minHeight:120 }}/>
    </Field>
    <div style={{ display:'flex', justifyContent:'space-between', marginTop:14 }}>
      <Btn v="ghost" onClick={regen} disabled={busy}>{busy ? <Spinner sz={14}/> : '↻ Regenerate'}</Btn>
      <div style={{ display:'flex', gap:8 }}>
        <Btn onClick={()=>save(false)}>Save Draft</Btn>
        <Btn v="primary" onClick={()=>save(true)}>Mark Published</Btn>
      </div>
    </div>
    <div style={{ fontSize:11, color:G.textDim, marginTop:10 }}>
      Google Business Profile API integration for one-click publishing requires verified-owner OAuth (manual setup per account). For now, copy this response and paste into Google's reply box.
    </div>
  </Modal>;
}
